import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Dashboard.css';
import logo from '../assets/DifferentialPay_tp.svg';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// --- Status badge component (reusable) ---
function StatusBadge({ status }) {
  const statusConfig = {
    DRAFT: { label: 'Draft', className: 'badge-draft' },
    PENDING_APPROVAL: { label: 'Pending Approval', className: 'badge-pending' },
    APPROVED: { label: 'Approved', className: 'badge-approved' },
    REJECTED: { label: 'Rejected', className: 'badge-rejected' },
    PAYMENT_PROCESSING: { label: 'Processing', className: 'badge-processing' },
    PAYMENT_COMPLETED: { label: 'Completed', className: 'badge-completed' },
  };
  const config = statusConfig[status] || { label: status, className: 'badge-draft' };
  return <span className={`status-badge ${config.className}`}>{config.label}</span>;
}

// --- Notification type config ---
const notificationConfig = {
  SUPPLIER_PENDING_APPROVAL: { icon: '👥', color: '#f39c12' },
  SUPPLIER_APPROVED: { icon: '✅', color: '#27ae60' },
  SUPPLIER_REJECTED: { icon: '❌', color: '#e74c3c' },
  BATCH_PENDING_APPROVAL: { icon: '📋', color: '#f39c12' },
  BATCH_APPROVED: { icon: '✅', color: '#27ae60' },
  BATCH_REJECTED: { icon: '❌', color: '#e74c3c' },
  PAYMENT_MARKED_PAID: { icon: '💰', color: '#27ae60' },
  POP_MISSING: { icon: '⚠️', color: '#e67e22' },
};

// --- Action item icon mapping ---
const actionIcons = {
  users: '👥',
  'file-check': '📋',
  'credit-card': '💳',
  'file-warning': '⚠️',
};

function Dashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [dashboardData, setDashboardData] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch dashboard stats and notifications in parallel
      const [statsRes, notifRes] = await Promise.all([
        fetch(`${API_URL}/api/dashboard/stats/`, {
          headers: { 'Authorization': `Token ${token}` }
        }),
        fetch(`${API_URL}/api/notifications/?limit=10`, {
          headers: { 'Authorization': `Token ${token}` }
        })
      ]);

      if (!statsRes.ok || !notifRes.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const statsData = await statsRes.json();
      const notifData = await notifRes.json();

      setDashboardData(statsData);
      setNotifications(notifData);
    } catch (err) {
      console.error('Dashboard error:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const response = await fetch(`${API_URL}/api/notifications/mark-all-read/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${token}` }
      });
      if (response.ok) {
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      }
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.is_read) {
      try {
        await fetch(`${API_URL}/api/notifications/${notification.id}/read/`, {
          method: 'POST',
          headers: { 'Authorization': `Token ${token}` }
        });
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
        );
      } catch (err) {
        console.error('Failed to mark as read:', err);
      }
    }
    // Navigate to the linked page
    if (notification.link) {
      navigate(notification.link);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <p>{error}</p>
        <button onClick={fetchDashboardData} className="btn-retry">Retry</button>
      </div>
    );
  }

  const { action_items, stats, recent_batches } = dashboardData;
  const unreadNotifications = notifications.filter(n => !n.is_read);

  return (
    <div className="dashboard">
      {/* Page Header */}
      <div className="dashboard-header">
        <div>
          <h1>Welcome back, {user?.first_name || 'User'}</h1>
          <p className="dashboard-subtitle">Here's what's happening with your payments</p>
        </div>
        <img src={logo} alt="DifferentialPay" className="dashboard-logo" />
      </div>

      {/* Action Required Panel */}
      {action_items.length > 0 && (
        <div className="dashboard-section">
          <h2 className="section-title">🔔 Action Required</h2>
          <div className="action-items-grid">
            {action_items.map((item, index) => (
              <div 
                key={index}
                className={`action-card action-${item.priority}`}
                onClick={() => navigate(item.link)}
              >
                <div className="action-icon">
                  {actionIcons[item.icon] || '📌'}
                </div>
                <div className="action-content">
                  <span className="action-count">{item.count}</span>
                  <span className="action-label">{item.label}</span>
                </div>
                <div className="action-arrow">→</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="dashboard-section">
        <h2 className="section-title">📈 Quick Stats</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-number">{stats.total_suppliers}</div>
            <div className="stat-label">Total Suppliers</div>
            <div className="stat-detail">
              {stats.approved_suppliers} approved · {stats.pending_suppliers} pending
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.total_batches}</div>
            <div className="stat-label">Total Batches</div>
            <div className="stat-detail">
              {stats.draft_batches} draft · {stats.pending_approval_batches} pending
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.approved_batches}</div>
            <div className="stat-label">Approved Batches</div>
            <div className="stat-detail">Ready for payment</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.completed_batches}</div>
            <div className="stat-label">Completed</div>
            <div className="stat-detail">Payments finalised</div>
          </div>
        </div>
      </div>

      {/* Two-column layout: Notifications + Recent Batches */}
      <div className="dashboard-columns">
        {/* Recent Notifications */}
        <div className="dashboard-section">
          <div className="section-header-row">
            <h2 className="section-title">🔔 Recent Notifications</h2>
            {unreadNotifications.length > 0 && (
              <button className="btn-mark-read" onClick={handleMarkAllRead}>
                Mark all as read
              </button>
            )}
          </div>
          <div className="notifications-list">
            {notifications.length === 0 ? (
              <div className="empty-state">No notifications yet</div>
            ) : (
              notifications.map(notification => {
                const config = notificationConfig[notification.notification_type] || {};
                return (
                  <div 
                    key={notification.id}
                    className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="notification-icon">{config.icon || '📌'}</div>
                    <div className="notification-content">
                      <div className="notification-title">{notification.title}</div>
                      <div className="notification-message">{notification.message}</div>
                      <div className="notification-time">{notification.time_ago}</div>
                    </div>
                    {!notification.is_read && <div className="unread-dot"></div>}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Recent Batches */}
        <div className="dashboard-section">
          <div className="section-header-row">
            <h2 className="section-title">📦 Recent Batches</h2>
            <button className="btn-view-all" onClick={() => navigate('/batches')}>
              View all →
            </button>
          </div>
          <div className="batches-table-container">
            <table className="batches-table">
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Status</th>
                  <th>Payment Date</th>
                  <th>Payments</th>
                  <th>Created By</th>
                </tr>
              </thead>
              <tbody>
                {recent_batches.map(batch => (
                  <tr 
                    key={batch.id} 
                    className="batch-row"
                    onClick={() => navigate(`/batches/${batch.id}`)}
                  >
                    <td className="batch-ref">{batch.batch_reference}</td>
                    <td><StatusBadge status={batch.status} /></td>
                    <td>
                      {batch.payment_date 
                        ? new Date(batch.payment_date).toLocaleDateString('en-ZA')
                        : '—'}
                    </td>
                    <td>{batch.payment_count}</td>
                    <td>{batch.created_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;